// =============================================================================
// MeuDriver — Instructors Page (orchestrator)
// -----------------------------------------------------------------------------
// Filtros, comparação e listagem foram movidos para `./instructors/`.
// Esta página orquestra estado de URL + render principal.
// =============================================================================

import { useState, useCallback, useEffect, useRef } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import {
  Search,
  SlidersHorizontal,
  X,
  Clock,
  CalendarDays,
  GitCompare,
  ArrowRight,
  LayoutGrid,
  Map,
} from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'

import { useInstructors } from '@/hooks/useInstructors'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useSEO } from '@/hooks/useSEO'
import { SPECIALTY_LABELS } from '@/lib/constants'
import { InstructorCard } from '@/components/instructor/InstructorCard'
import { InstructorCardSkeleton } from '@/components/instructor/InstructorCardSkeleton'
import { InstructorMapView } from '@/components/instructor/InstructorMapView'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { cn } from '@/lib/utils'
import { track } from '@/lib/analytics'

import { FilterPanel, type FilterPanelProps } from './instructors/InstructorFilters'
import { CompareModal } from './instructors/InstructorCompareModal'

type SortOption = 'rating_desc' | 'price_asc' | 'price_desc' | 'experience_desc'

export default function InstructorsPage() {
  useSEO({
    title: 'Encontre Instrutores de Direção',
    description:
      'Pesquise e compare instrutores de direção particulares na sua cidade. Avaliações reais, preços transparentes e agendamento online.',
  })

  const [searchParams, setSearchParams] = useSearchParams()
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false)
  const [sort, setSort] = useState<SortOption>('rating_desc')
  const [compareIds, setCompareIds] = useState<Set<string>>(new Set())
  const [compareOpen, setCompareOpen] = useState(false)
  const [viewMode, setViewMode] = useState<'grid' | 'map'>('grid')
  // U3 — filtro de tags (client-side)
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([])

  const search               = searchParams.get('search') ?? ''
  const city                 = searchParams.get('city') ?? ''
  const specialties          = searchParams.get('specialty')?.split(',').filter(Boolean) ?? []
  const minPrice             = searchParams.get('minPrice') ?? ''
  const maxPrice             = searchParams.get('maxPrice') ?? ''
  const minRating            = Number(searchParams.get('minRating') ?? 0)
  const availableToday       = searchParams.get('availableToday') === '1'
  const availableThisWeek    = searchParams.get('availableThisWeek') === '1'
  const availableDate        = searchParams.get('availableDate') ?? ''
  const womenOnly            = searchParams.get('womenOnly') === '1'
  const onlyPro              = searchParams.get('onlyPro') === '1'
  // P1 — filtro de experiência mínima
  const minExperienceYears   = Number(searchParams.get('minExp') ?? 0)

  const updateParam = useCallback(
    (key: string, value: string) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev)
        if (value) next.set(key, value)
        else next.delete(key)
        return next
      })
    },
    [setSearchParams],
  )

  const handleSpecialtyToggle = useCallback(
    (key: string) => {
      const next = specialties.includes(key)
        ? specialties.filter((s) => s !== key)
        : [...specialties, key]
      updateParam('specialty', next.join(','))
    },
    [specialties, updateParam],
  )

  const handleTagToggle = useCallback((tagId: string) => {
    setSelectedTagIds((prev) =>
      prev.includes(tagId) ? prev.filter((t) => t !== tagId) : [...prev, tagId],
    )
  }, [])

  const handleClearFilters = () => {
    setSearchParams(new URLSearchParams())
    setSelectedTagIds([])
  }

  const { instructors: allInstructors, isLoading } = useInstructors({
    search, city, specialties,
    minPrice: minPrice ? Number(minPrice) : undefined,
    maxPrice: maxPrice ? Number(maxPrice) : undefined,
    minRating: minRating || undefined,
    sort,
    availableToday: availableToday || undefined,
    availableThisWeek: availableThisWeek || undefined,
    availableDate: availableDate || undefined,
    womenOnly: womenOnly || undefined,
    onlyPro: onlyPro || undefined,
    minExperienceYears: minExperienceYears || undefined,
  })

  // U3 — Busca IDs de instrutores que possuem as tags selecionadas (filtragem server-side)
  const { data: tagFilteredIds } = useQuery<string[] | null>({
    queryKey: ['instructor-tag-filter', selectedTagIds],
    enabled: selectedTagIds.length > 0,
    queryFn: async () => {
      // Para cada tag, busca os instructor_ids; intersecção garante que tem TODAS as tags
      const results = await Promise.all(
        selectedTagIds.map((tagId) =>
          supabase
            .from('instructor_tags')
            .select('instructor_id')
            .eq('tag_id', tagId)
            .then(({ data }) => new Set((data ?? []).map((r) => r.instructor_id as string))),
        ),
      )
      // Intersecção dos sets
      const [first, ...rest] = results
      if (!first) return null
      const intersection = rest.reduce((acc, set) => {
        return new Set([...acc].filter((id) => set.has(id)))
      }, first)
      return Array.from(intersection)
    },
    staleTime: 1000 * 60,
  })

  // Aplica filtragem client-side quando há tags selecionadas
  const instructors =
    selectedTagIds.length > 0 && tagFilteredIds
      ? allInstructors.filter((inst) => tagFilteredIds.includes(inst.id))
      : allInstructors

  const activeFilterCount = [
    search, city, ...specialties, minPrice, maxPrice,
    minRating > 0 ? '1' : '',
    availableToday ? '1' : '',
    availableThisWeek ? '1' : '',
    availableDate,
    womenOnly ? '1' : '',
    onlyPro ? '1' : '',
    minExperienceYears > 0 ? '1' : '',
    ...selectedTagIds,
  ].filter(Boolean).length

  // Posthog: emite `search_performed` quando o usuário aplica algum filtro
  // (com debounce implícito — só captura quando os params parram de mudar
  // por 500ms). Evita avalanche em digitação.
  const trackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => {
    if (activeFilterCount === 0) return
    if (trackTimerRef.current) clearTimeout(trackTimerRef.current)
    trackTimerRef.current = setTimeout(() => {
      track('search_performed', {
        has_city: Boolean(city),
        specialty_count: specialties.length,
        has_price_range: Boolean(minPrice || maxPrice),
        min_rating: minRating || undefined,
        available_today: availableToday || undefined,
        available_this_week: availableThisWeek || undefined,
        has_available_date: Boolean(availableDate),
      })
    }, 500)
    return () => {
      if (trackTimerRef.current) clearTimeout(trackTimerRef.current)
    }
  }, [activeFilterCount, city, specialties.length, minPrice, maxPrice, minRating, availableToday, availableThisWeek, availableDate])

  const handleCompareToggle = (id: string) => {
    setCompareIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else if (next.size < 3) next.add(id)
      return next
    })
  }

  const compareList = instructors.filter((i) => compareIds.has(i.id))

  const filterPanelProps: FilterPanelProps = {
    search, city, specialties, minPrice, maxPrice, minRating, availableDate, womenOnly, onlyPro,
    minExperienceYears,
    selectedTagIds,
    onTagToggle: handleTagToggle,
    onSearchChange: (v) => updateParam('search', v),
    onCityChange: (v) => updateParam('city', v),
    onSpecialtyToggle: handleSpecialtyToggle,
    onMinPriceChange: (v) => updateParam('minPrice', v),
    onMaxPriceChange: (v) => updateParam('maxPrice', v),
    onMinRatingChange: (v) => updateParam('minRating', v > 0 ? String(v) : ''),
    onAvailableDateChange: (v) => updateParam('availableDate', v),
    onWomenOnlyChange: (v) => updateParam('womenOnly', v ? '1' : ''),
    onOnlyProChange: (v) => updateParam('onlyPro', v ? '1' : ''),
    onMinExperienceYearsChange: (v) => updateParam('minExp', v > 0 ? String(v) : ''),
    onClear: handleClearFilters,
  }

  // Não-usado, mas mantém Link import-stable em diff
  void Link

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <h1 className="text-3xl font-bold text-gray-900">Encontre seu instrutor</h1>
          <p className="mt-2 text-gray-500">Instrutores qualificados em todo o Brasil, prontos para te ajudar.</p>

          <div className="flex items-center gap-2 mt-4 flex-wrap">
            <button
              onClick={() => {
                updateParam('availableToday', availableToday ? '' : '1')
                if (!availableToday) updateParam('availableThisWeek', '')
              }}
              className={cn(
                'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border transition-all',
                availableToday
                  ? 'bg-brand-600 text-white border-brand-600'
                  : 'bg-white text-gray-600 border-gray-200 hover:border-brand-300 hover:text-brand-600',
              )}
            >
              <Clock className="h-3.5 w-3.5" />
              Disponível hoje
            </button>
            <button
              onClick={() => {
                updateParam('availableThisWeek', availableThisWeek ? '' : '1')
                if (!availableThisWeek) updateParam('availableToday', '')
              }}
              className={cn(
                'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border transition-all',
                availableThisWeek
                  ? 'bg-brand-600 text-white border-brand-600'
                  : 'bg-white text-gray-600 border-gray-200 hover:border-brand-300 hover:text-brand-600',
              )}
            >
              <CalendarDays className="h-3.5 w-3.5" />
              Disponível esta semana
            </button>

            {compareIds.size > 0 && (
              <span className="text-xs text-muted-foreground ml-auto">
                {compareIds.size} selecionado{compareIds.size > 1 ? 's' : ''} para comparar
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex gap-8">
          <aside className="hidden lg:block w-72 flex-shrink-0">
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 sticky top-6">
              <h2 className="text-base font-semibold text-gray-900 mb-5">Filtros</h2>
              <FilterPanel {...filterPanelProps} />
            </div>
          </aside>

          <main className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-6 gap-4">
              <div className="flex items-center gap-3">
                <Sheet open={mobileFiltersOpen} onOpenChange={setMobileFiltersOpen}>
                  <SheetTrigger asChild>
                    <Button variant="outline" className="lg:hidden gap-2" size="sm">
                      <SlidersHorizontal className="h-4 w-4" />
                      Filtros
                      {activeFilterCount > 0 && (
                        <Badge className="ml-1 h-5 w-5 p-0 flex items-center justify-center text-xs bg-brand-600">
                          {activeFilterCount}
                        </Badge>
                      )}
                    </Button>
                  </SheetTrigger>
                  <SheetContent side="left" className="w-80 overflow-y-auto">
                    <SheetHeader className="mb-6">
                      <SheetTitle>Filtros</SheetTitle>
                    </SheetHeader>
                    <FilterPanel {...filterPanelProps} />
                  </SheetContent>
                </Sheet>

                <p className="text-sm text-gray-500">
                  {isLoading ? (
                    <span className="inline-block h-4 w-28 bg-gray-200 rounded animate-pulse" />
                  ) : (
                    <>
                      <span className="font-semibold text-gray-900">{instructors.length}</span>{' '}
                      {instructors.length === 1 ? 'instrutor encontrado' : 'instrutores encontrados'}
                    </>
                  )}
                </p>
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                <div className="flex items-center rounded-lg border border-gray-200 bg-white p-0.5 gap-0.5">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={cn(
                      'flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-all',
                      viewMode === 'grid' ? 'bg-brand-600 text-white shadow-sm' : 'text-gray-500 hover:text-gray-700',
                    )}
                    title="Visualização em grade"
                  >
                    <LayoutGrid className="h-3.5 w-3.5" />
                    <span className="hidden sm:inline">Grade</span>
                  </button>
                  <button
                    onClick={() => setViewMode('map')}
                    className={cn(
                      'flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-all',
                      viewMode === 'map' ? 'bg-brand-600 text-white shadow-sm' : 'text-gray-500 hover:text-gray-700',
                    )}
                    title="Visualização no mapa"
                  >
                    <Map className="h-3.5 w-3.5" />
                    <span className="hidden sm:inline">Mapa</span>
                  </button>
                </div>

                <Select value={sort} onValueChange={(v) => setSort(v as SortOption)}>
                  <SelectTrigger className="w-44 h-9 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="rating_desc">Mais bem avaliados</SelectItem>
                    <SelectItem value="price_asc">Menor preço</SelectItem>
                    <SelectItem value="price_desc">Maior preço</SelectItem>
                    <SelectItem value="experience_desc">Mais experientes</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {activeFilterCount > 0 && (
              <div className="flex flex-wrap gap-2 mb-5">
                {search && (
                  <Badge variant="secondary" className="gap-1.5 cursor-pointer hover:bg-gray-200" onClick={() => updateParam('search', '')}>
                    Busca: {search}<X className="h-3 w-3" />
                  </Badge>
                )}
                {city && (
                  <Badge variant="secondary" className="gap-1.5 cursor-pointer hover:bg-gray-200" onClick={() => updateParam('city', '')}>
                    Cidade: {city}<X className="h-3 w-3" />
                  </Badge>
                )}
                {specialties.map((s) => (
                  <Badge key={s} variant="secondary" className="gap-1.5 cursor-pointer hover:bg-gray-200" onClick={() => handleSpecialtyToggle(s)}>
                    {SPECIALTY_LABELS[s as keyof typeof SPECIALTY_LABELS] ?? s}<X className="h-3 w-3" />
                  </Badge>
                ))}
                {(minPrice || maxPrice) && (
                  <Badge variant="secondary" className="gap-1.5 cursor-pointer hover:bg-gray-200"
                    onClick={() => { updateParam('minPrice', ''); updateParam('maxPrice', '') }}>
                    Preço: R${minPrice || '0'} – R${maxPrice || '∞'}<X className="h-3 w-3" />
                  </Badge>
                )}
                {minRating > 0 && (
                  <Badge variant="secondary" className="gap-1.5 cursor-pointer hover:bg-gray-200" onClick={() => updateParam('minRating', '')}>
                    {minRating}+ ★<X className="h-3 w-3" />
                  </Badge>
                )}
                {availableToday && (
                  <Badge variant="secondary" className="gap-1.5 cursor-pointer hover:bg-gray-200 bg-brand-50 text-brand-700" onClick={() => updateParam('availableToday', '')}>
                    <Clock className="h-3 w-3" />Disponível hoje<X className="h-3 w-3" />
                  </Badge>
                )}
                {availableThisWeek && (
                  <Badge variant="secondary" className="gap-1.5 cursor-pointer hover:bg-gray-200 bg-brand-50 text-brand-700" onClick={() => updateParam('availableThisWeek', '')}>
                    <CalendarDays className="h-3 w-3" />Esta semana<X className="h-3 w-3" />
                  </Badge>
                )}
                {onlyPro && (
                  <Badge variant="secondary" className="gap-1.5 cursor-pointer hover:bg-gray-200 bg-brand-50 text-brand-700" onClick={() => updateParam('onlyPro', '')}>
                    Pro<X className="h-3 w-3" />
                  </Badge>
                )}
              </div>
            )}

            {viewMode === 'map' && <InstructorMapView instructors={instructors} />}

            {viewMode === 'grid' && (
              isLoading ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
                  {Array.from({ length: 6 }).map((_, i) => <InstructorCardSkeleton key={i} />)}
                </div>
              ) : instructors.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
                  {instructors.map((instructor) => (
                    <InstructorCard
                      key={instructor.id}
                      instructor={instructor}
                      isComparing={compareIds.has(instructor.id)}
                      onCompareToggle={() => handleCompareToggle(instructor.id)}
                    />
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-24 text-center">
                  <div className="rounded-full bg-gray-100 p-6 mb-4">
                    <Search className="h-10 w-10 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">Nenhum instrutor encontrado</h3>
                  <p className="text-gray-500 max-w-sm">
                    Tente ajustar os filtros ou ampliar a sua busca para encontrar instrutores disponíveis na sua região.
                  </p>
                  <Button type="button" variant="outline" className="mt-6" onClick={handleClearFilters}>
                    Limpar filtros
                  </Button>
                </div>
              )
            )}
          </main>
        </div>
      </div>

      <AnimatePresence>
        {compareIds.size >= 2 && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 400, damping: 30 }}
            className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-4 bg-slate-900 text-white px-5 py-3 rounded-2xl shadow-2xl border border-white/10"
          >
            <div className="flex items-center gap-2">
              <GitCompare className="h-4 w-4 text-brand-400" />
              <span className="text-sm font-medium">
                {compareIds.size} instrutor{compareIds.size > 1 ? 'es' : ''} selecionado{compareIds.size > 1 ? 's' : ''}
              </span>
              {compareIds.size < 3 && (
                <span className="text-xs text-white/40">(selecione até {3 - compareIds.size} mais)</span>
              )}
            </div>
            <Button size="sm" className="bg-brand-600 hover:bg-brand-500 text-white h-8 text-xs px-4" onClick={() => setCompareOpen(true)}>
              Comparar
              <ArrowRight className="h-3.5 w-3.5 ml-1" />
            </Button>
            <button onClick={() => setCompareIds(new Set())} className="text-white/40 hover:text-white/80 transition-colors">
              <X className="h-4 w-4" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      <CompareModal instructors={compareList} open={compareOpen} onClose={() => setCompareOpen(false)} />
    </div>
  )
}
